import { z } from "zod";

/** 個人の ToDo ボード(SPEC §10)。列はポータル独自で、取り込み元(Backlog 等)の状態は変えない。 */
export const TODO_TITLE_MAX = 200;
export const TODO_NOTE_MAX = 2000;

export const todoColumnSchema = z.enum(["TODO", "DOING", "DONE"]);
export type TodoColumnValue = z.infer<typeof todoColumnSchema>;

export const todoSourceTypeSchema = z.enum(["BACKLOG"]);
export type TodoSourceTypeValue = z.infer<typeof todoSourceTypeSchema>;

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD で指定してください");

export const todoCardViewSchema = z.object({
  id: z.string(),
  column: todoColumnSchema,
  title: z.string(),
  note: z.string(),
  dueDate: dateSchema.nullable(),
  /** 自動で入ったカードの出どころ。手書きは null。 */
  source: z
    .object({
      type: todoSourceTypeSchema,
      /** 取り込み元での ID(Backlog は課題キー)。 */
      externalId: z.string(),
      url: z.url(),
      project: z.string().nullable(),
      /** 取り込み元のプロジェクトの識別子(Backlog はプロジェクトキー)。 */
      projectKey: z.string().nullable().default(null),
      status: z.string().nullable(),
      priority: z.enum(["HIGH", "NORMAL", "LOW"]).nullable(),
      syncedAt: z.iso.datetime(),
    })
    .nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TodoCardView = z.infer<typeof todoCardViewSchema>;

/** 取り込み元の状態。error は今回の取り込みに失敗した(前回までのカードはそのまま)。 */
export const todoSourceViewSchema = z.object({
  type: todoSourceTypeSchema,
  status: z.enum(["disconnected", "connected", "error"]),
  syncedAt: z.iso.datetime().nullable(),
  /** 上限に達して入れられなかった件数。 */
  skipped: z.number().int().nonnegative(),
});
export type TodoSourceView = z.infer<typeof todoSourceViewSchema>;

/** cards は列ごとの並び順(列の中では配列の順)。sources は連携が有効な取り込み元だけ。 */
export const todoBoardResponseSchema = z.object({
  cards: z.array(todoCardViewSchema),
  sources: z.array(todoSourceViewSchema),
});
export type TodoBoardResponse = z.infer<typeof todoBoardResponseSchema>;

export const createTodoCardRequestSchema = z.object({
  title: z.string().trim().min(1, "タスク名を入力してください").max(TODO_TITLE_MAX),
  note: z.string().max(TODO_NOTE_MAX).optional(),
  dueDate: dateSchema.nullable().optional(),
  column: todoColumnSchema.optional(),
});
export type CreateTodoCardRequest = z.infer<typeof createTodoCardRequestSchema>;

/**
 * 取り込んだカードは note と column だけ変えられる(タスク名・期限は取り込み元に合わせて上書きされる)。
 * column を変えると、その列の末尾へ動く。
 */
export const updateTodoCardRequestSchema = z.object({
  title: z.string().trim().min(1, "タスク名を入力してください").max(TODO_TITLE_MAX).optional(),
  note: z.string().max(TODO_NOTE_MAX).optional(),
  dueDate: dateSchema.nullable().optional(),
  column: todoColumnSchema.optional(),
});
export type UpdateTodoCardRequest = z.infer<typeof updateTodoCardRequestSchema>;

/** 列と、列の中での位置(0 始まり)。 */
export const moveTodoCardRequestSchema = z.object({
  column: todoColumnSchema,
  index: z.number().int().min(0).max(10_000),
});
export type MoveTodoCardRequest = z.infer<typeof moveTodoCardRequestSchema>;

/** 取り込みから除外できるプロジェクト(本人が参加しているもの)。excluded は、いま除外しているか。 */
export const todoSourceProjectsResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("disconnected") }),
  z.object({
    status: z.literal("connected"),
    projects: z.array(z.object({ key: z.string(), name: z.string(), excluded: z.boolean() })),
  }),
]);
export type TodoSourceProjectsResponse = z.infer<typeof todoSourceProjectsResponseSchema>;

/** 取り込まないプロジェクト(置き換え。Backlog はプロジェクトキー)。 */
export const updateExcludedProjectsRequestSchema = z.object({
  projectKeys: z.array(z.string().trim().min(1).max(100)).max(200),
});
export type UpdateExcludedProjectsRequest = z.infer<typeof updateExcludedProjectsRequestSchema>;
